import { isMobileQuery } from "../utils/utils"

const CENTER = [174.7762, -41.2865]
const DEFAULT_ZOOM = 4.8
const DETAIL_ZOOM = 10
const TEXT_SIZE = 18
const TEXT_SIZE_MOBILE = 13
const TEXT_FIT_PADDING = [10, 8, 10, 8]
const TEXT_FIT_PADDING_MOBILE = [5, 8, 5, 8]
const ICON_RATIO = 1
const ICON_RATIO_MOBILE = 0.75

class OfficesMap {
  /* global mapboxgl */
  constructor(mapElem) {
    this.mapElem = mapElem
    this.accessToken = this.mapElem.dataset.accessToken
    this.offices = JSON.parse(this.mapElem.dataset.offices)
    this.popup = null
    this.map = null
    this.labelMarkers = []

    this.setMap()
      .loadImages()
      .drawFeatures()
      .initEventListener()

    isMobileQuery.addEventListener("change", this.setLayersMarkersProperties.bind(this))
  }

  setMap() {
    mapboxgl.accessToken = this.accessToken

    this.map = new mapboxgl.Map({
      container: this.mapElem.getAttribute("id"),
      style: "mapbox://styles/somar-design-studios/ckrlhca4d2nsy19pmf391yo7k", // style URL
      center: CENTER, // starting position [lng, lat]
      zoom: DEFAULT_ZOOM, // starting zoom
    })

    this.map.on("load", () => {
      //mapboxgl-ctrl-top-right
      this.mapElem
        .querySelector(".offices-map__map-controls")
        .appendChild(this.mapElem.querySelector(".mapboxgl-ctrl-top-right"))
    })

    return this
  }

  loadImages() {
    // background for offices labels
    this.map.loadImage("_resources/app/client/dist/images/white-45x350.png", (error, image) => {
      if (error) throw error
      if (!this.map.hasImage("white-pixel")) {
        this.map.addImage("white-pixel", image)
      }
    })

    return this
  }

  drawFeatures() {
    this.map.on("load", () => {
      this.map.resize()
      this.map.scrollZoom.disable()
      this.map.touchZoomRotate.enable()

      // Add zoom and rotation controls to the map.
      this.map.addControl(new mapboxgl.NavigationControl())

      this.map.addSource("offices", {
        type: "geojson",
        data: this.offices,
        cluster: true,
        clusterMaxZoom: 14, // Max zoom to cluster points on
        clusterRadius: 85, // Radius of each cluster when clustering points
      })

      /**
       * Yellow cluster circles showing number of offices in the area.
       */
      this.map.addLayer({
        id: "clusters",
        type: "circle",
        source: "offices",
        filter: ["has", "point_count"],
        paint: {
          // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
          // with three steps to implement three types of circles:
          //   * yellow80, 20px circles when point count is less than 3
          //   * yellow100, 30px circles when point count is between 3 and 5
          //   * yellow120, 40px circles when point count is greater than or equal to 5
          "circle-color": [
            "step",
            ["get", "point_count"],
            "#f8d671", // yellow80
            3,
            "#ffcb34", // yellow100
            5,
            "#efb303", // yellow120
          ],
          "circle-radius": ["step", ["get", "point_count"], 20, 3, 30, 5, 40],
        },
      })

      /**
       * Numbers on top of the yellow cluster circles.
       */
      this.map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "offices",
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
        },
      })

      /**
       * Text labels for the offices.
       */
      this.map.addLayer({
        id: "offices",
        type: "symbol",
        source: "offices",
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": "white-pixel",
          "icon-text-fit": "both",
          "icon-text-fit-padding": isMobileQuery.matches ? TEXT_FIT_PADDING_MOBILE : TEXT_FIT_PADDING,
          "icon-allow-overlap": true,
          "text-field": "{name}",
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": isMobileQuery.matches ? TEXT_SIZE_MOBILE : TEXT_SIZE,
          "text-anchor": "left",
          "text-offset": [2.8, -2.1],
        },
        paint: {
          "text-color": "#074c9b",
        },
      })

      /**
       * CSNZ logos for each office.
       */
      this.map.addLayer({
        id: "offices-icons",
        type: "symbol",
        source: "offices",
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": "office-location",
          "icon-anchor": "bottom",
          "icon-allow-overlap": true,
          "icon-size": isMobileQuery.matches ? ICON_RATIO_MOBILE : ICON_RATIO,
        },
      })
    })

    return this
  }

  initEventListener() {
    // change mouse cursor on hover
    const setPointer = style => (this.map.getCanvas().style.cursor = style)

    ;["clusters", "offices", "offices-icons"].forEach(layer => {
      this.map.on("mouseenter", layer, () => {
        setPointer("pointer")
      })
      this.map.on("mouseleave", layer, () => {
        setPointer("")
      })
    })

    // inspect a cluster on click
    this.map.on("click", "clusters", e => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ["clusters"],
      })
      const clusterId = features[0].properties.cluster_id

      this.map.getSource("offices").getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return

        this.map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
        })
      })
    })

    // go to the office detail page
    ;["offices", "offices-icons"].forEach(layer => {
      this.map.on("click", layer, e => {
        window.location = e.features[0].properties.link
      })
    })

    document.getElementById("offices-map-select").addEventListener("choice", event => {
      const choiceValue = event.detail.choice.value

      if (choiceValue === "user-location") {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(pos => {
            this.map.flyTo({
              center: [pos.coords.longitude, pos.coords.latitude],
              zoom: DETAIL_ZOOM,
            })
          })
        }
      } else {
        let flyToOpt = {}
        const officeFilter = this.offices.features.filter(e => e.properties.id == event.detail.choice.value)
        const office = Array.isArray(officeFilter) ? officeFilter[0] : null

        if (office) {
          flyToOpt.center = office.geometry.coordinates
          flyToOpt.zoom = DETAIL_ZOOM
        } else {
          flyToOpt.center = CENTER
          flyToOpt.zoom = DEFAULT_ZOOM
        }

        this.map.flyTo(flyToOpt)
      }
    })

    return this
  }

  setLayersMarkersProperties() {
    this.map.setLayoutProperty("offices", "text-size", isMobileQuery.matches ? TEXT_SIZE_MOBILE : TEXT_SIZE)
    this.map.setLayoutProperty(
      "offices",
      "icon-text-fit-padding",
      isMobileQuery.matches ? TEXT_FIT_PADDING_MOBILE : TEXT_FIT_PADDING
    )
    this.map.setLayoutProperty("offices-icons", "icon-size", isMobileQuery.matches ? ICON_RATIO_MOBILE : ICON_RATIO)
  }
}

export default function init() {
  if (document.querySelector(".offices-map")) {
    new OfficesMap(document.querySelector(".offices-map"))
  }
}
